import type { Ref } from 'preact';
import { ArrowUp, Check, Lock, MoreHorizontal, Smile, X } from 'lucide-preact';
import { formatRelativeTime } from '@pinmerge/shared';
import type { LocalComment } from '../storage';
import type { ReviewerCache } from '../identity';
import type { Active } from '../lib/types';
import { pinPosition, viewportAnchor } from '../lib/pin-geometry';
import { isOwnComment } from '../lib/comments';
import { REACTION_EMOJIS, formatAbsoluteTime, formatReactorList } from '../lib/format';
import { AvatarSlot } from './AvatarSlot';
import { t } from '../i18n';

export interface PinPopoverProps {
  active: Active;
  comments: LocalComment[];
  reviewer: ReviewerCache | null;
  reviewerName: string;
  loggedIn: boolean;
  canPickWithoutLogin: boolean;
  ownToken: string;
  projectSlug: string | null;
  linkCopiedFor: string | null;
  apiUrl: string;
  scroll: { x: number; y: number };
  error: string | null;
  pinRef: Ref<HTMLDivElement>;
  // Compose / edit textarea state (shared between compose comment, edit
  // comment, edit reply — see App.tsx for the orchestration). Reply composer
  // uses its own state below.
  text: string;
  setText: (v: string) => void;
  textareaRef: Ref<HTMLTextAreaElement>;
  // Reply composer state.
  replyText: string;
  setReplyText: (v: string) => void;
  replyTextareaRef: Ref<HTMLTextAreaElement>;
  // Pop-over UI state.
  editingReplyId: string | null;
  setEditingReplyId: (v: string | null) => void;
  pickerOpenFor: string | null;
  setPickerOpenFor: (v: string | null) => void;
  menuOpenFor: string | null;
  setMenuOpenFor: (v: string | null) => void;
  failedAvatars: Set<string>;
  onAvatarFailed: (slug: string) => void;
  setActive: (a: Active) => void;
  // Action callbacks (delegate to useThreads in the parent).
  onSaveDraft: () => void;
  onSaveEdit: () => void;
  onRemove: (id: string) => void;
  onSubmitReply: (parent: LocalComment) => void;
  onSaveReplyEdit: () => void;
  onRemoveReply: (id: string) => void;
  onReactionClick: (commentId: string, remoteId: string, emoji: string) => void;
  onCopyPermalink: (remoteId: string) => void;
  onStartEditing: () => void;
  onStartEditingReply: (id: string) => void;
  onCloseAll: () => void;
  onOpenLogin: () => void;
}

export function PinPopover(props: PinPopoverProps) {
  const {
    active,
    comments,
    reviewer,
    reviewerName,
    loggedIn,
    canPickWithoutLogin,
    ownToken,
    projectSlug,
    linkCopiedFor,
    apiUrl,
    scroll,
    error,
    pinRef,
    text,
    setText,
    textareaRef,
    replyText,
    setReplyText,
    replyTextareaRef,
    editingReplyId,
    setEditingReplyId,
    pickerOpenFor,
    setPickerOpenFor,
    menuOpenFor,
    setMenuOpenFor,
    failedAvatars,
    onAvatarFailed,
    setActive,
    onSaveDraft,
    onSaveEdit,
    onRemove,
    onSubmitReply,
    onSaveReplyEdit,
    onRemoveReply,
    onReactionClick,
    onCopyPermalink,
    onStartEditing,
    onStartEditingReply,
    onCloseAll,
    onOpenLogin,
  } = props;

  const activeComment =
    active?.kind === 'view' ? comments.find((c) => c.id === active.id) ?? null : null;
  const activeIsMine = activeComment ? isOwnComment(activeComment, ownToken) : true;
  const activeAnchor: { vx: number; vy: number } | null =
    active?.kind === 'new'
      ? { vx: active.draft.docX - scroll.x, vy: active.draft.docY - scroll.y }
      : activeComment
        ? viewportAnchor(activeComment, scroll)
        : null;
  const activePos = activeAnchor ? pinPosition(activeAnchor) : null;
  const isEditing = active?.kind === 'view' && active.editing;
  const isComposing = active?.kind === 'new';

  if (!active || !activePos) return null;

  function renderReactionsRow(c: LocalComment) {
    if (!c.remoteId) return null;
    const reactions = (c.reactions ?? []).filter((r) => r.count > 0);
    const pickerOpen = pickerOpenFor === c.id;
    if (reactions.length === 0 && !pickerOpen) return null;
    return (
      <div class={`reactions ${pickerOpen ? 'reactions--picker-open' : ''}`}>
        {reactions.map((r) => (
          <button
            key={r.emoji}
            type="button"
            class={`reaction ${r.mine ? 'reaction--mine' : ''}`}
            onClick={() => onReactionClick(c.id, c.remoteId!, r.emoji)}
            title={formatReactorList(r.reactor_names ?? [], r.mine, reviewerName)}
          >
            <span class="reaction__emoji">{r.emoji}</span>
            <span class="reaction__count">{r.count}</span>
          </button>
        ))}
        {pickerOpen && (
          <div class="reaction-picker" onClick={(e) => e.stopPropagation()}>
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                class="reaction-picker__btn"
                onClick={() => {
                  onReactionClick(c.id, c.remoteId!, emoji);
                  setPickerOpenFor(null);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  function avatar(args: {
    avatarId: string | null;
    avatarBg: string | null;
    authorName: string;
    isOther?: boolean;
    extraClass?: string;
  }) {
    return (
      <AvatarSlot
        apiUrl={apiUrl}
        avatarId={args.avatarId}
        avatarBg={args.avatarBg}
        authorName={args.authorName}
        isOther={args.isOther}
        extraClass={args.extraClass}
        failedAvatars={failedAvatars}
        onAvatarFailed={onAvatarFailed}
      />
    );
  }

  function renderReplyRow(reply: LocalComment) {
    const mine = isOwnComment(reply, ownToken);
    const editing = editingReplyId === reply.id;
    return (
      <div key={reply.id} class="pin__comment">
        <div class="pin__comment-head">
          {avatar({
            avatarId: reply.avatar?.id ?? null,
            avatarBg: reply.avatar?.bg ?? null,
            authorName: reply.authorName,
            isOther: !mine,
          })}
          <span class="pin__author-name">{reply.authorName}</span>
          <span class="pin__time" title={formatAbsoluteTime(reply.createdAt)}>
            {formatRelativeTime(reply.createdAt)}
          </span>
          {!editing && reply.remoteId && (
            <div class="comment-actions">
              <button
                class="comment-action-btn"
                title={t('pin.react')}
                aria-label={t('pin.react')}
                onClick={(e) => {
                  e.stopPropagation();
                  setPickerOpenFor(pickerOpenFor === reply.id ? null : reply.id);
                  setMenuOpenFor(null);
                }}
              >
                <Smile size={14} />
              </button>
              {mine && (
                <button
                  class="comment-action-btn"
                  title={t('pin.more-options')}
                  aria-label={t('pin.more-options')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenFor(menuOpenFor === reply.id ? null : reply.id);
                    setPickerOpenFor(null);
                  }}
                >
                  <MoreHorizontal size={14} />
                </button>
              )}
              {menuOpenFor === reply.id && (
                <div class="comment-menu" onClick={(e) => e.stopPropagation()}>
                  <button
                    class="comment-menu__item"
                    onClick={() => {
                      setMenuOpenFor(null);
                      onStartEditingReply(reply.id);
                    }}
                  >
                    {t('pin.edit')}
                  </button>
                  <button
                    class="comment-menu__item comment-menu__item--danger"
                    onClick={() => {
                      setMenuOpenFor(null);
                      onRemoveReply(reply.id);
                    }}
                  >
                    {t('pin.delete')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <textarea
            class="textarea"
            aria-label={t('pin.edit')}
            value={text}
            onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                onSaveReplyEdit();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setEditingReplyId(null);
                setText('');
              }
            }}
            rows={2}
          />
        ) : (
          <div
            class="pin__body"
            onDblClick={mine ? () => onStartEditingReply(reply.id) : undefined}
            title={mine ? t('pin.dblclick-edit') : ''}
          >
            {reply.text}
          </div>
        )}
        {!editing && renderReactionsRow(reply)}
        {editing && (
          <div class="pin__actions">
            <button
              class="btn btn--ghost btn--small"
              onClick={() => {
                setEditingReplyId(null);
                setText('');
              }}
            >
              {t('pin.cancel')}
            </button>
            <button
              class="btn btn--primary btn--small"
              onClick={onSaveReplyEdit}
              disabled={!text.trim()}
            >
              {t('pin.save')}
            </button>
          </div>
        )}
      </div>
    );
  }

  function renderReplyComposer(parent: LocalComment) {
    // Always rendered. Disabled while the parent is still syncing (no remoteId
    // yet) — the API rejects replies to unsynced parents anyway.
    const synced = !!parent.remoteId;
    const canSubmit = synced && !!replyText.trim() && !!reviewer;
    return (
      <div class="reply-composer">
        <div class="composer-input">
          <textarea
            ref={replyTextareaRef}
            class="textarea"
            placeholder={synced ? t('pin.reply-placeholder') : t('pin.reply-placeholder-syncing')}
            value={replyText}
            disabled={!synced}
            onInput={(e) => setReplyText((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                onSubmitReply(parent);
              }
            }}
            rows={2}
          />
          <button
            class="composer-send"
            type="button"
            title={t('pin.reply')}
            aria-label={t('pin.reply')}
            disabled={!canSubmit}
            onClick={() => onSubmitReply(parent)}
          >
            <ArrowUp size={14} strokeWidth={2} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={pinRef}
      class={`pin pin--${activePos.arrow} ${activeComment && !activeIsMine ? 'pin--other' : ''}`}
      style={`left: ${activePos.left}px; top: ${activePos.top}px;`}
      onClick={(e) => {
        e.stopPropagation();
        // Click anywhere inside the pin that isn't the picker/menu or its
        // trigger closes any open popover. The triggers themselves stop
        // propagation so this only fires for outside-clicks.
        const t = e.target as HTMLElement | null;
        if (
          pickerOpenFor !== null &&
          t &&
          !t.closest('.reaction-picker') &&
          !t.closest('.comment-action-btn')
        ) {
          setPickerOpenFor(null);
        }
        if (
          menuOpenFor !== null &&
          t &&
          !t.closest('.comment-menu') &&
          !t.closest('.comment-action-btn')
        ) {
          setMenuOpenFor(null);
        }
      }}
    >
      {/* Compose state: provisional pin, no chrome bar — just a textarea +
          footer. Only ever rendered when the user is signed in (the picker mode
          itself is gated on loggedIn), but we double-guard here so a stray
          draft doesn't show a blank composer. */}
      {isComposing && reviewer && (
        <>
          <div class="pin__head">
            <div class="pin__author">
              {avatar({
                avatarId: reviewer.avatar_id ?? null,
                avatarBg: reviewer.avatar_bg ?? null,
                authorName: reviewerName || '',
              })}
              <span class="pin__author-meta">
                <span class="pin__author-name">{reviewerName || t('pin.new-comment')}</span>
              </span>
            </div>
            <button class="pin__close" onClick={onCloseAll} title={t('pin.discard')} aria-label={t('pin.discard-label')}>
              <X size={16} />
            </button>
          </div>
          <div class="composer-input">
            <textarea
              ref={textareaRef}
              class="textarea"
              placeholder={t('pin.composer-placeholder')}
              value={text}
              onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  onSaveDraft();
                }
              }}
              rows={3}
            />
            <button
              class="composer-send"
              type="button"
              title={t('pin.send')}
              aria-label={t('pin.send')}
              disabled={!text.trim()}
              onClick={onSaveDraft}
            >
              <ArrowUp size={14} strokeWidth={2} />
            </button>
          </div>
          {error && <div class="error">{error}</div>}
        </>
      )}

      {/* View state: chrome bar (title + close) over a comment row, body,
          reactions, replies, and an always-visible reply composer. Per-comment
          hover icons (Smile + MoreHorizontal) appear on hover. When the viewer
          is not signed in, the body / replies / composer area is replaced by
          a lock card. */}
      {active.kind === 'view' && activeComment && (
        <>
          <div class="pin__chrome">
            <span class="pin__chrome-title">{t('pin.chrome-title')}</span>
            {loggedIn && activeComment.remoteId && projectSlug && (
              <div class="comment-actions">
                <button
                  class="comment-action-btn"
                  title={t('pin.more-options')}
                  aria-label={t('pin.more-options')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenFor(menuOpenFor === '__thread__' ? null : '__thread__');
                    setPickerOpenFor(null);
                  }}
                >
                  <MoreHorizontal size={14} />
                </button>
                {menuOpenFor === '__thread__' && (
                  <div class="comment-menu" onClick={(e) => e.stopPropagation()}>
                    <button
                      class="comment-menu__item"
                      onClick={() => onCopyPermalink(activeComment.remoteId!)}
                    >
                      {linkCopiedFor === activeComment.remoteId ? (
                        <span class="comment-menu__check">
                          <Check size={12} /> {t('pin.link-copied')}
                        </span>
                      ) : (
                        t('pin.copy-link')
                      )}
                    </button>
                    {activeIsMine && (
                      <button
                        class="comment-menu__item comment-menu__item--danger"
                        onClick={() => {
                          setMenuOpenFor(null);
                          onRemove(activeComment.id);
                        }}
                      >
                        {t('pin.archive')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <button class="pin__close" onClick={onCloseAll} title={t('pin.close')} aria-label={t('pin.close-label')}>
              <X size={16} />
            </button>
          </div>

          {canPickWithoutLogin ? (
            <div class="pin__comment">
              <div class="pin__comment-head">
                {avatar({
                  avatarId: activeComment.avatar?.id ?? null,
                  avatarBg: activeComment.avatar?.bg ?? null,
                  authorName: activeComment.authorName,
                  isOther: !activeIsMine,
                })}
                <span class="pin__author-name">{activeComment.authorName}</span>
                <span class="pin__time" title={formatAbsoluteTime(activeComment.createdAt)}>
                  {formatRelativeTime(activeComment.createdAt)}
                </span>
                {!isEditing && activeComment.remoteId && (
                  <div class="comment-actions">
                    <button
                      class="comment-action-btn"
                      title={t('pin.react')}
                      aria-label={t('pin.react')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPickerOpenFor(pickerOpenFor === activeComment.id ? null : activeComment.id);
                        setMenuOpenFor(null);
                      }}
                    >
                      <Smile size={14} />
                    </button>
                    {activeIsMine && (
                      <button
                        class="comment-action-btn"
                        title={t('pin.more-options')}
                        aria-label={t('pin.more-options')}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenFor(menuOpenFor === activeComment.id ? null : activeComment.id);
                          setPickerOpenFor(null);
                        }}
                      >
                        <MoreHorizontal size={14} />
                      </button>
                    )}
                    {menuOpenFor === activeComment.id && (
                      <div class="comment-menu" onClick={(e) => e.stopPropagation()}>
                        <button
                          class="comment-menu__item"
                          onClick={() => {
                            setMenuOpenFor(null);
                            onStartEditing();
                          }}
                        >
                          {t('pin.edit')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!isEditing && activeComment.thumbnailUrl && (
                <a
                  class="pin__thumb"
                  href={activeComment.thumbnailUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={t('pin.open-fullsize')}
                >
                  <img src={activeComment.thumbnailUrl} alt="" loading="lazy" />
                </a>
              )}

              {!isEditing ? (
                <div
                  class="pin__body"
                  onDblClick={activeIsMine ? onStartEditing : undefined}
                  title={activeIsMine ? t('pin.dblclick-edit') : t('pin.other-author')}
                >
                  {activeComment.text}
                </div>
              ) : (
                <textarea
                  ref={textareaRef}
                  class="textarea"
                  aria-label={t('pin.edit')}
                  value={text}
                  onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      onSaveEdit();
                    }
                  }}
                  rows={3}
                />
              )}

              {!isEditing && renderReactionsRow(activeComment)}
            </div>
          ) : (
            <div class="pin__locked">
              <div class="pin__locked-icon">
                <Lock size={20} aria-hidden="true" />
              </div>
              <div class="pin__locked-title">{t('pin.locked-title')}</div>
              <div class="pin__locked-sub">
                {t('pin.locked-sub')}
              </div>
              <button class="btn btn--primary" onClick={onOpenLogin}>
                {t('pin.signin')}
              </button>
            </div>
          )}

          {loggedIn && error && <div class="error">{error}</div>}

          {/* Replies + composer. Composer is always rendered when not in edit
              mode; it disables itself if the parent hasn't synced yet. */}
          {loggedIn && !isEditing && (() => {
            const replies = comments.filter((c) => c.parentId === activeComment.id);
            return (
              <div class="replies">
                {replies.map((reply) => renderReplyRow(reply))}
                {renderReplyComposer(activeComment)}
              </div>
            );
          })()}

          {/* Edit-mode footer. Non-editing Bearbeiten/Archivieren actions live
              in the per-comment hover menu above. */}
          {loggedIn && isEditing && (
            <div class="pin__actions">
              <button
                class="btn btn--ghost"
                onClick={() => {
                  setText(activeComment.text);
                  setActive({ kind: 'view', id: activeComment.id, editing: false });
                }}
              >
                {t('pin.cancel')}
              </button>
              <button class="btn btn--primary" onClick={onSaveEdit} disabled={!text.trim()}>
                {t('pin.save')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
